import { useEffect, lazy, Suspense } from "react";
import HeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const CitiesDirectory = lazy(() => import("./components/CitiesDirectory"));
const StateResources = lazy(() => import("./components/StateResources"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const FinalCTA = lazy(() => import("./components/FinalCTA"));
import { faqs } from "./components/FAQSection";

export default function DelawarePage() {
  useEffect(() => {
    document.title =
      "Expedited & Emergency Passport Services Delaware | Fast Passport Renewal DE | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    setMeta(
      "description",
      "Fast Passport Center provides emergency, expedited, and same-day passport services across Delaware — Wilmington, Dover, Newark, and beyond. As fast as 24 hours. A+ BBB rated, registered State Dept. courier."
    );
    setMeta(
      "keywords",
      "expedited passport Delaware, emergency passport Delaware, same day passport DE, fast passport Wilmington, passport renewal Dover, passport service Newark Delaware"
    );
    setMeta("geo.region", "US-DE");
    setMeta("geo.placename", "Delaware");
    setMeta("geo.position", "38.9108;-75.5277");
    setMeta("ICBM", "38.9108, -75.5277");
    setLink("canonical", "https://fastpassportcenter.com/delaware");

    const pageUrl = "https://fastpassportcenter.com/delaware";

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for Delaware travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for Delaware U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for Delaware residents with lost or stolen passports." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
    ];

    let schema = document.getElementById("de-schema");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "de-schema";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }

    schema.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Delaware", item: pageUrl },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "Fast Passport Center - Delaware",
        description:
          "Expedited and emergency passport services across Delaware — Wilmington, Dover, Newark, and every community in between.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        email: "info@fastpassportcenter.com",
        priceRange: "$$",
        areaServed: { "@type": "State", name: "Delaware" },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "08:00",
            closes: "20:00",
          },
        ],
      },
      ...servicesList.map((s) => ({
        "@context": "https://schema.org",
        "@type": "Service",
        name: s.name,
        description: s.description,
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "State", name: "Delaware" },
      })),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: { "@type": "Answer", text: faq.answer },
        })),
      },
    ]);

    return () => {
      document.getElementById("de-schema")?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <HeroSection />
      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-gray-50">
            <div className="animate-pulse text-gray-400 text-sm">Loading cities...</div>
          </div>
        }
      >
        <CitiesDirectory />
      </Suspense>

      <section className="bg-[#F7F8FA] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Passport Services
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              How We Serve Delaware
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              From Wilmington&apos;s corporate banking corridor to the Delaware beaches, from Dover Air Force Base to the University of Delaware campus — we help First State travelers get their passports fast. No regional agency in Delaware means residents rely on Philadelphia or DC. Our courier service eliminates the drive entirely.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "ri-flight-takeoff-line",
                title: "Emergency Same-Day Service",
                desc: "For Delaware travelers with flights within 72 hours. We hand-carry your application to the nearest regional agency.",
              },
              {
                icon: "ri-time-line",
                title: "Expedited 2–9 Business Days",
                desc: "Standard expedited processing with overnight shipping and tracking for all Delaware residents.",
              },
              {
                icon: "ri-file-list-3-line",
                title: "New Passport Applications",
                desc: "First-time applicants in Delaware — we handle document prep, photos, and acceptance facility scheduling.",
              },
              {
                icon: "ri-refresh-line",
                title: "Passport Renewals",
                desc: "Delaware renewals by mail or courier. We ensure your application is complete to avoid delays.",
              },
              {
                icon: "ri-user-add-line",
                title: "Child & Minor Passports",
                desc: "Delaware families with kids under 16 — we guide you through parental consent and documentation.",
              },
              {
                icon: "ri-error-warning-line",
                title: "Lost, Stolen & Damaged",
                desc: "Fast replacement for Delaware residents who need a new passport quickly.",
              },
            ].map((svc, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5">
                  <i className={`${svc.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-white">
            <div className="animate-pulse text-gray-400 text-sm">Loading resources...</div>
          </div>
        }
      >
        <StateResources />
      </Suspense>
      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-gray-50">
            <div className="animate-pulse text-gray-400 text-sm">Loading FAQs...</div>
          </div>
        }
      >
        <FAQSection />
      </Suspense>

      <section className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <SEOCrossLinks state="DE" showCities={false} />
        </div>
      </section>

      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-[#0F2240]">
            <div className="animate-pulse text-gray-400 text-sm">Loading...</div>
          </div>
        }
      >
        <FinalCTA />
      </Suspense>
    </div>
  );
}